import { motion } from 'framer-motion';
import { FiArrowRight, FiLinkedin, FiMail, FiMapPin } from 'react-icons/fi';
import profileImage from '../sushilpic.jpeg';
import { SiteConfig } from '../types/portfolio';
import Icon from './Icon';

type HeroProps = {
  siteConfig: SiteConfig;
};

function Hero({ siteConfig }: HeroProps) {
  return (
    <section id="home" className="relative overflow-hidden pt-32 sm:pt-36">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative mx-auto w-full max-w-md lg:mx-0"
        >
          <div className="glass-panel rounded-[2rem] p-4">
            <div className="relative overflow-hidden rounded-[1.75rem]">
            <div className="absolute inset-0 bg-gradient-to-br from-sky-400/20 via-transparent to-teal-400/10" />
            <img
              src={profileImage}
              alt="Portrait of Sushil Kumar"
              className="relative h-[28rem] w-full rounded-[1.6rem] object-cover object-top"
            />
            </div>
            <div className="mt-4 rounded-3xl border border-white/10 bg-slate-950/70 p-5 backdrop-blur-xl">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-sky-200">Enterprise Engineering Focus</p>
                  <p className="mt-2 text-lg font-semibold text-white">Java, React, Microservices</p>
                </div>
                <span className="w-fit rounded-full border border-teal-400/25 bg-teal-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-teal-200">
                  Premium Profile
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative"
        >
          <div className="inline-flex items-center rounded-full border border-sky-400/20 bg-sky-400/10 px-4 py-2 text-sm font-medium text-sky-200">
            {siteConfig.experienceLabel}
          </div>
          <h1 className="mt-6 text-4xl font-semibold tracking-tight text-white sm:text-6xl">
            {siteConfig.name}
          </h1>
          <p className="mt-5 text-xl font-medium text-slate-200 sm:text-2xl">{siteConfig.title}</p>
          <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300">{siteConfig.tagline}</p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200">
            <Icon icon={FiMapPin} className="text-sky-300" />
            Based in {siteConfig.location}
          </div>

          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#projects" className="primary-button">
              View Projects
              <Icon icon={FiArrowRight} />
            </a>
            <a href="#contact" className="secondary-button">
              <Icon icon={FiMail} />
              Contact
            </a>
            <a
              href={siteConfig.linkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="secondary-button"
            >
              <Icon icon={FiLinkedin} />
              LinkedIn
            </a>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {siteConfig.stats.map((stat) => (
              <div key={stat.label} className="glass-panel rounded-3xl p-5">
                <p className="text-2xl font-semibold text-white">{stat.value}</p>
                <p className="mt-2 text-sm leading-6 text-slate-300">{stat.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Hero;
